import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Image } from '@change/ds-core-ui';
import { GroupsIcon } from '@change/ds-icons/GroupsIcon';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { FacebookGroupRecommendation } from '#src/app/pages/psf/shared/api';

import { RadioCard } from '../RadioCard';

type Props = {
	groups: readonly FacebookGroupRecommendation[];
	/** The selected group's URL. Group recommendations carry no id, so the URL identifies a card. */
	selectedUrl: string | null;
	onSelect: (group: FacebookGroupRecommendation) => void;
	/** Called once per card each time the list mounts, with the card's group URL. */
	onCardViewed: (groupUrl: string) => void;
};

const GroupThumbnail: React.FC<{ imageUrl: string | null }> = ({ imageUrl }) => {
	const [imageError, setImageError] = useState(false);

	if (!imageUrl || imageError) {
		return (
			<span className="bg-fill-selected-weaker text-icon-selected flex size-12 items-center justify-center rounded-md">
				<GroupsIcon size="small" />
			</span>
		);
	}

	return (
		<Image src={imageUrl} alt="" className="size-12 rounded-md object-cover" onError={() => setImageError(true)} />
	);
};

/**
 * The `variant_2` group step: the petition's cause-matched Facebook groups, one radio card each. It
 * shows no copied pill: the destination step showed it already.
 */
export const GroupCardList: React.FC<Props> = ({ groups, selectedUrl, onSelect, onCardViewed }) => {
	const { translate } = useI18n();

	// The list mounts once per visit to the group step. The ref keeps a re-render, a new `groups`
	// identity, or React's development double effect from firing the viewed events twice.
	const viewedRef = useRef(false);
	useEffect(() => {
		if (viewedRef.current) return;
		viewedRef.current = true;
		groups.forEach((group) => onCardViewed(group.url));
	}, [groups, onCardViewed]);

	return (
		<fieldset className="m-0 flex w-full flex-col gap-2 border-0 p-0" data-testid="facebook-post-sheet-groups">
			<legend className="sr-only">
				{translate('corgi.psf.promote_or_share.facebook_post_sheet.group_list_label')}
			</legend>
			{groups.map((group) => (
				<RadioCard
					key={group.url}
					name="facebook-post-group"
					value={group.url}
					checked={group.url === selectedUrl}
					onChange={() => onSelect(group)}
					leading={<GroupThumbnail imageUrl={group.imageUrl} />}
					label={group.name}
					showIndicator
					testId="facebook-post-sheet-group-card"
				/>
			))}
		</fieldset>
	);
};
